import { useCallback } from 'react';
import { LatLng } from '../../../types/Geo';
import { MarkerProperties } from '../../../types/CustomRoute';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { getMarkerSelectionProperties, selectCustomRouteActiveSegment, updateActiveSegmentHandle } from '../../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../../redux/customRoutesApi';
import useRouting from '../useRouting';

const useRerouteMarker = () => {
  const dispatch = useAppDispatch();
  const { activeSegment, selectedHandleIndex } = useAppSelector(state => ({
    activeSegment: selectCustomRouteActiveSegment(state),
    selectedHandleIndex: state.customRoute.selectedHandleIndex
  }));
  const routing = useRouting();

  const rerouteMarker = useCallback(
    async (marker: MarkerProperties) => {
      if (!activeSegment) {
        return;
      }

      const { latitude, longitude } = marker;
      const { isStart, isEnd, pathIndex } = getMarkerSelectionProperties(marker, selectedHandleIndex);

      let points;

      // There is some repetition in this if statement but it is structured to explicitly map marker type to behavior
      if (isStart) {
        const path = activeSegment.paths[0];
        points = [[latitude, longitude], path.points[path.points.length - 1]];
      } else if (isEnd) {
        const path = activeSegment.paths[activeSegment.paths.length - 1];
        points = [path.points[0], [latitude, longitude]];
      } else {
        const prevPath = activeSegment.paths[pathIndex - 1];
        const path = activeSegment.paths[pathIndex];
        points = [prevPath.points[0], [latitude, longitude], path.points[path.points.length - 1]];
      }

      const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes(points) });
      const newPaths = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths;

      if (!isSuccess || !newPaths?.length) {
        return;
      }

      let beforePoints: LatLng[] | undefined;
      let afterPoints: LatLng[] | undefined;

      if (!isStart) {
        beforePoints = newPaths[0].points;
      }

      if (isStart) {
        afterPoints = newPaths[0].points;
      } else if (!isEnd) {
        afterPoints = newPaths[1].points;
      }

      dispatch(updateActiveSegmentHandle({ ...marker, beforePoints, afterPoints }));
    },
    [activeSegment, selectedHandleIndex, routing, dispatch]
  );

  return rerouteMarker;
};

export default useRerouteMarker;
